import { useCallback } from 'react';
import { FieldValues, useController } from 'react-hook-form';

import AutocompleteBase from './AutocompleteBase';
import { AsyncAutocompleteFieldProps } from './types';
import useAsyncAutocompleteField from './useAsyncAutocompleteField';

const INFINITE_SCROLL_OFFSET_TRIGGER_IN_PX = 300;

export default function AsyncAutocompleteField<
  T extends FieldValues,
  ReadType,
  ParsedType,
  FetchExtraArguments extends Record<string, unknown>,
>({
  control,
  excludedOptionValues,
  extraOptions,
  fetchFn,
  name,
  onSearchChange,
  onValueChange,
  parseFn,
  queryName,
  search,
  ...rest
}: AsyncAutocompleteFieldProps<T, ReadType, ParsedType, FetchExtraArguments>) {
  const { field, fieldState } = useController({ control, name });
  const error = fieldState.error?.message ?? fieldState.error?.root?.message;

  const autocompleteState = useAsyncAutocompleteField({
    queryName: `${queryName}_${name}`,
    fetchFn,
    initialInputValue: field.value?.label,
    onSearchChange,
    parseFn,
    search,
  });

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLUListElement>) => {
      const target = event.currentTarget as HTMLElement;
      const scrollThreshold = target.scrollHeight - INFINITE_SCROLL_OFFSET_TRIGGER_IN_PX;
      const scrolledOffset = target.scrollTop + target.clientHeight;

      if (scrolledOffset >= scrollThreshold) {
        autocompleteState.fetchMore();
      }
    },
    [autocompleteState.fetchMore],
  );

  return (
    <AutocompleteBase
      ListboxProps={{ onScroll: handleScroll }}
      error={error}
      filterOptions={(options) => options}
      inputValue={autocompleteState.inputValue}
      isLoading={autocompleteState.isFetching}
      isOpen={autocompleteState.isOpen}
      name={name}
      onChange={(value) => {
        field.onChange(value);
        onValueChange?.(value);
      }}
      onClose={autocompleteState.handleClose}
      onInputChange={autocompleteState.handleInputChange}
      onOpen={autocompleteState.handleOpen}
      options={[...(extraOptions ?? []), ...autocompleteState.options].filter(
        (option) => !excludedOptionValues?.includes(option.value),
      )}
      value={field.value}
      {...rest}
    />
  );
}
